export const normalizeLinkHref = (value = '') => {
  const href = typeof value === 'string' ? value.trim() : '';

  if (!href) {
    return '';
  }

  if (/^(https?:|mailto:|tel:)/i.test(href) || href.startsWith('#')) {
    return href;
  }

  if (href.startsWith('//')) {
    return `https:${href}`;
  }

  if (/^[a-z][a-z\d+.-]*:/i.test(href)) {
    return '';
  }

  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(href)) {
    return `mailto:${href}`;
  }

  return `https://${href}`;
};

export const normalizeHtmlLinks = (html = '') => {
  if (!html || typeof document === 'undefined') {
    return html;
  }

  const container = document.createElement('div');
  container.innerHTML = html;

  container.querySelectorAll('a[href]').forEach((link) => {
    const normalizedHref = normalizeLinkHref(link.getAttribute('href'));

    if (normalizedHref) {
      link.setAttribute('href', normalizedHref);
    } else {
      link.removeAttribute('href');
    }
  });

  return container.innerHTML;
};

export const normalizeEmailListSpacing = (html = '') => {
  if (!html || typeof document === 'undefined') {
    return html;
  }

  const container = document.createElement('div');
  container.innerHTML = html;

  container.querySelectorAll('ul, ol').forEach((list) => {
    list.style.marginTop = '0';
    list.style.marginBottom = '0';
    list.style.paddingLeft = '24px';
  });

  container.querySelectorAll('li').forEach((listItem) => {
    listItem.style.marginTop = '0';
    listItem.style.marginBottom = '0';
    listItem.style.paddingTop = '0';
    listItem.style.paddingBottom = '0';
  });

  container.querySelectorAll('li > p').forEach((paragraph) => {
    paragraph.style.marginTop = '0';
    paragraph.style.marginBottom = '0';
  });

  return container.innerHTML;
};

// A blank line reaches us in several shapes depending on where it came from:
// `<p></p>` when the user pressed Enter in the editor, `<p>&nbsp;</p>` or
// `<p><span>&nbsp;</span></p>` when it was pasted out of Word or Outlook. They
// used to be handled by two different regexes in two different places, which is
// why blank lines rendered differently in the preview than in the sent email.
// Everything now collapses to a single `<p>&nbsp;</p>`.
const isBlankParagraph = (paragraph) => {
  if (paragraph.querySelector('img, table, hr, ul, ol')) {
    return false;
  }

  // More than one <br> means the author asked for more than one blank line.
  if (paragraph.querySelectorAll('br').length > 1) {
    return false;
  }

  return (paragraph.textContent || '').replace(/\u00a0/g, ' ').trim() === '';
};

export const normalizeEmailBlankLines = (html = '') => {
  if (!html || typeof document === 'undefined') {
    return html;
  }

  const container = document.createElement('div');
  container.innerHTML = html;

  container.querySelectorAll('p').forEach((paragraph) => {
    if (isBlankParagraph(paragraph)) {
      paragraph.innerHTML = '&nbsp;';
    }
  });

  return container.innerHTML;
};

// Older templates were authored with `target="_self"`, and a few carry
// alignment that the email wrapper cannot honour. Both the preview and the sent
// email need this applied identically.
export const normalizeEmailAttributes = (html = '') => {
  if (!html) {
    return html;
  }

  return html
    .replace(/target="_self"/g, 'target="_blank" rel="noopener noreferrer"')
    .replaceAll('text-align:center;', 'text-align:left;')
    .replaceAll('text-align:right;', 'text-align:left;')
    .replaceAll('justify-content:center;', 'justify-content:left;')
    .replaceAll('justify-content:right;', 'justify-content:left;');
};
